import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'

import Select from '@components/select/select'
import Button from '@components/button/button'
import CsvPreview from '@components/csvPreview/CsvPreview'
import Swal from 'sweetalert2'

import apis from '@app/api/api'
import downloadCsv from '@utils/CSVutils'

interface Props {
  adminCompanies: Option[]
  adminIntUsers: Option[]
  adminExtUsers: Option[]
}

const EXPORT_OPTIONS = ['internal', 'external', 'company'] as const

const UserExport = ({ adminCompanies, adminIntUsers, adminExtUsers }: Props) => {
  const i18n = useI18n()

  const [selectedExport, setSelectedExport] = React.useState<typeof EXPORT_OPTIONS[number]>(EXPORT_OPTIONS[0])
  const [selectedOption, setSelectedOption] = React.useState<Option>()

  const [fileContent, setFileContent] = React.useState<any>()
  const [loading, setLoading] = React.useState<boolean>(false)

  const exportDeclarations = () => {
    if (selectedOption) {
      const searchType = selectedExport === 'company' ? 'company' : 'user'

      setLoading(true)
      apis.admin.export
        .declarations({ type: searchType, searchId: selectedOption.value })
        .then((res) => {
          if (!res) {
            Swal.fire({
              title: i18n.t('components.adminData.noexport.title'),
              text: i18n.t('components.adminData.noexport.text'),
              icon: 'info',
              showCancelButton: false,
              confirmButtonColor: '#3A8641',
              confirmButtonText: i18n.t('components.adminData.noexport.confirm'),
            })
          } else {
            setFileContent(res)
            downloadCsv(
              res,
              selectedExport === 'external'
                ? 'declarations_externe.csv'
                : selectedExport === 'internal'
                ? 'declarations_interne.csv'
                : 'declarations_entreprise.csv'
            )
          }
          setLoading(false)
        })
        .catch(() => {
          Swal.fire({
            title: i18n.t('components.adminData.errorexport.title'),
            text: i18n.t('components.adminData.errorexport.text'),
            icon: 'error',
            showCancelButton: false,
            confirmButtonColor: '#3A8641',
            confirmButtonText: i18n.t('components.adminData.errorexport.confirm'),
          })
          setLoading(false)
        })
    }
  }

  const Content = () => {
    switch (selectedExport) {
      case 'internal':
        return (
          <FormItemContainer>
            <SelectorContainer>
              <SelectorTitle>{i18n.t('components.adminData.userInt')}</SelectorTitle>
              <Select
                selected={selectedOption}
                options={adminIntUsers}
                setSelected={setSelectedOption}
                isClearable
                placeholder={i18n.t('components.adminData.placeholders.user')}
                noMargin
              />
            </SelectorContainer>
          </FormItemContainer>
        )
      case 'external':
        return (
          <FormItemContainer>
            <SelectorContainer>
              <SelectorTitle>{i18n.t('components.adminData.userExt')}</SelectorTitle>
              <Select
                selected={selectedOption}
                options={adminExtUsers}
                setSelected={setSelectedOption}
                isClearable
                placeholder={i18n.t('components.adminData.placeholders.user')}
                noMargin
              />
            </SelectorContainer>
          </FormItemContainer>
        )
      case 'company':
        return (
          <FormItemContainer>
            <SelectorContainer>
              <SelectorTitle>{i18n.t('components.adminData.company')}</SelectorTitle>
              <Select
                selected={selectedOption}
                options={adminCompanies}
                setSelected={setSelectedOption}
                isClearable
                placeholder={i18n.t('components.adminData.placeholders.company')}
                noMargin
              />
            </SelectorContainer>
          </FormItemContainer>
        )
      default:
        return null
    }
  }

  return (
    <>
      <SectionTitleContainer>{i18n.t('components.adminData.sectionTitles.declarationExport')}</SectionTitleContainer>
      <RowContainer>
        <FormItemContainer>
          <SelectionContainer>
            {EXPORT_OPTIONS.map((itm, idx) => (
              <SelectionItem
                key={idx}
                selected={itm === selectedExport}
                onClick={() => {
                  if (selectedExport !== itm) {
                    setSelectedOption(undefined)
                    setSelectedExport(itm)
                  }
                }}
                first={idx === 0}
                last={idx === EXPORT_OPTIONS.length - 1}>
                <SelectionLibelle selected={itm === selectedExport}>
                  {i18n.t(`components.adminData.exporttype.${itm}`)}
                </SelectionLibelle>
              </SelectionItem>
            ))}
          </SelectionContainer>
        </FormItemContainer>
      </RowContainer>
      <RowContainer>
        <Content />
      </RowContainer>
      <ButtonContainer>
        <Button
          title={i18n.t('components.adminData.export')}
          onClick={exportDeclarations}
          type="action"
          status={loading ? 'loading' : !!selectedOption ? 'active' : 'disabled'}
        />
      </ButtonContainer>
      {fileContent && <CsvPreview csv={fileContent} />}
    </>
  )
}

export default UserExport

const SectionTitleContainer = styled('div')`
  ${(props) => props.theme.fonts.sectionTitle};
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  align-items: center;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')<{ center?: boolean }>`
  display: flex;
  ${(props) => props.center && 'align-items: center'}
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  ${(props) => props.theme.fonts.selectorTitle};
`
const ButtonContainer = styled('div')`
  margin: 25px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

// Selection du type d'export
const RADIUS = 10
const SelectionContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 50px;
  border-radius: ${RADIUS}px;
  background-color: white;
  box-shadow: 1px 1px 2px 1px #2224;
`
const SelectionItem = styled('div')<{ selected: boolean; first: boolean; last: boolean }>`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.selected ? props.theme.colors.selectionItemSelected : props.theme.colors.selectionItemUnselected};

  ${(props) => props.last && `border-radius: 0 ${RADIUS}px ${RADIUS}px 0;`}
  ${(props) => props.first && `border-radius: ${RADIUS}px 0 0 ${RADIUS}px;`}
  cursor: pointer;
`
const SelectionLibelle = styled('div')<{ selected: boolean }>`
  ${(props) => props.theme.fonts.label};
  color: ${(props) =>
    props.selected ? props.theme.colors.selectionLibelleSelected : props.theme.colors.selectionLibelleUnselected};
  ${(props) => props.selected && 'font-weight: bold;'}
`
